import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Button, Grid, Chip, LinearProgress, Divider,
  AppBar, Toolbar, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, CircularProgress, Avatar, Tabs, Tab
} from '@mui/material';
import {
  ArrowBack, VerifiedUser, Add, Refresh, Star, Security, Timeline,
  CheckCircle, Warning, Error, PlayArrow, Stop, Edit, Delete
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import strictMonochromeTheme from '../utils/strictMonochromeTheme';
import { ethers } from 'ethers';

const Validators = () => {
  const navigate = useNavigate();
  const [validators, setValidators] = useState([]);
  const [myValidators, setMyValidators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [createDialog, setCreateDialog] = useState(false);
  const [networkStats, setNetworkStats] = useState({
    total_validators: 0,
    active_validators: 0,
    total_stake_avo: 0,
    avg_apr: 0
  });
  const [newValidator, setNewValidator] = useState({
    moniker: '',
    commission: '5',
    minSelfDelegation: '1000',
    description: '',
    website: '',
    securityContact: ''
  });

  // Función para obtener validadores de la red AVO
  const fetchValidators = useCallback(async () => {
    console.log('🔍 [VALIDATORS] Fetching validators from AVO RPC...');
    setLoading(true);
    
    try {
      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      console.log('📡 [VALIDATORS] Calling avo_getValidators...');
      const validatorsData = await provider.send('avo_getValidators', []);
      console.log('✅ [VALIDATORS] Raw RPC response:', validatorsData);
      
      if (!Array.isArray(validatorsData)) {
        console.error('❌ [VALIDATORS] Invalid response format:', validatorsData);
        setValidators([]);
        setMyValidators([]);
        setLoading(false);
        return;
      }

      // Obtener dirección del usuario actual
      const userAddress = localStorage.getItem('address')?.toLowerCase() || '';
      console.log('👤 [VALIDATORS] User address:', userAddress);

      // Mapear datos de RPC al formato del componente
      const mappedValidators = validatorsData.map((validator, index) => ({
        id: validator.id || index,
        operatorAddress: validator.address || `avovaloper${validator.id}...`,
        moniker: `Validator #${validator.id}`,
        status: validator.status === 'Active' ? 'active' : 'inactive',
        commission: 5, // Default commission
        votingPower: Math.round(validator.total_stake || 0),
        selfStake: Math.round(validator.own_stake || 0),
        totalStake: Math.round(validator.total_stake || 0),
        uptime: 99.0, // Default uptime
        jailed: false,
        tombstoned: false,
        description: `Validator ${validator.id} securing the AVO network`,
        website: '',
        rank: index + 1,
        delegatorCount: validator.delegator_count || 0,
        apr: (validator.apr * 100).toFixed(1),
        totalRewards: validator.total_rewards?.toFixed(2) || '0.00',
        createdAt: validator.created_at || 0,
        isOwned: validator.address?.toLowerCase() === userAddress
      }));

      console.log(`📋 [VALIDATORS] Processed ${mappedValidators.length} validators`);

      // Separar validadores propios de los de la red
      const myVals = mappedValidators.filter(v => v.isOwned);
      const networkVals = mappedValidators.filter(v => !v.isOwned);

      setValidators(networkVals);
      setMyValidators(myVals);

      // Calcular estadísticas de red
      const stats = {
        total_validators: mappedValidators.length,
        active_validators: mappedValidators.filter(v => v.status === 'active').length,
        total_stake_avo: mappedValidators.reduce((sum, v) => sum + v.totalStake, 0),
        avg_apr: mappedValidators.length > 0 
          ? (mappedValidators.reduce((sum, v) => sum + parseFloat(v.apr), 0) / mappedValidators.length).toFixed(1)
          : '0.0'
      };
      
      setNetworkStats(stats);
      console.log('📊 [VALIDATORS] Network stats:', stats);
      
    } catch (error) {
      console.error('❌ [VALIDATORS] Error fetching validators:', error);
      setValidators([]);
      setMyValidators([]);
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchValidators();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchValidators, 10000);
    return () => clearInterval(interval);
  }, [fetchValidators]);

  const getStatusColor = (status, jailed) => {
    if (jailed) return '#FFFFFF';
    switch (status) {
      case 'active': return '#FFFFFF';
      case 'inactive': return '#9E9E9E';
      case 'unbonding': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const handleCreateValidator = () => {
    alert('Validator creation will be implemented with AVO CLI/RPC connection');
    setCreateDialog(false);
    setNewValidator({
      moniker: '',
      commission: '5',
      minSelfDelegation: '1000',
      description: '',
      website: '',
      securityContact: ''
    });
  };

  const handleValidatorAction = (action, validator) => {
    alert(`${action} action for validator ${validator.moniker} will be implemented with AVO CLI connection`);
  };

  const ValidatorCard = ({ validator, isOwned = false }) => (
    <Card sx={{ mb: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: '#000000', 
              border: '1px solid #FFFFFF',
              color: '#FFFFFF',
              width: 40, 
              height: 40 
            }}>
              {validator.moniker.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                {validator.moniker}
                {isOwned && <Chip 
                  label="OWNED" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    backgroundColor: '#000000', 
                    border: '1px solid #FFFFFF',
                    color: '#FFFFFF',
                    borderRadius: 0
                  }} 
                />}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                Rank #{validator.rank} • {validator.operatorAddress.slice(0, 20)}...
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={validator.jailed ? 'JAILED' : validator.status.toUpperCase()} 
            sx={{ 
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              color: '#FFFFFF',
              fontWeight: 600,
              borderRadius: 0
            }}
          />
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>VOTING POWER</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {(validator.votingPower / 1000000).toFixed(2)}M AVO
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>COMMISSION</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {validator.commission}%
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>APR</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {validator.apr}%
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>DELEGATORS</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {validator.delegatorCount}
            </Typography>
          </Grid>
        </Grid>

        {validator.description && (
          <Typography variant="body2" sx={{ mb: 2, color: '#FFFFFF' }}>
            {validator.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1}>
          {isOwned ? (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit sx={{ color: '#FFFFFF' }} />}
                size="small"
                onClick={() => handleValidatorAction('EDIT', validator)}
                sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
              >
                EDIT
              </Button>
              <Button
                variant="outlined"
                startIcon={<Stop sx={{ color: '#FFFFFF' }} />}
                size="small"
                onClick={() => handleValidatorAction('UNJAIL', validator)}
                sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
              >
                UNJAIL
              </Button>
              <Button
                variant="outlined"
                startIcon={<Delete sx={{ color: '#FFFFFF' }} />}
                size="small"
                onClick={() => handleValidatorAction('DELETE', validator)}
                sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
              >
                REMOVE
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
              >
                DELEGATE
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
              >
                DETAILS
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );

  return (
    <ThemeProvider theme={strictMonochromeTheme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', color: '#FFFFFF' }}>
        {/* Header */}
        <AppBar position="static" sx={{ backgroundColor: '#000000', borderBottom: '1px solid #FFFFFF' }}>
          <Toolbar>
            <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#FFFFFF', mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <VerifiedUser sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, letterSpacing: '1px' }}>
              AVO VALIDATORS
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateDialog(true)}
              sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', mr: 2 }}
            >
              CREATE VALIDATOR
            </Button>
            <IconButton onClick={fetchValidators} sx={{ color: '#FFFFFF' }}>
              <Refresh />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <VerifiedUser sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkStats.total_validators}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  TOTAL VALIDATORS
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <CheckCircle sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkStats.active_validators}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  ACTIVE
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <Star sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {myValidators.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  MY VALIDATORS
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <Timeline sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkStats.avg_apr}%
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  AVG APR
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Card sx={{ 
            mb: 3, 
            backgroundColor: '#000000', 
            border: '1px solid #FFFFFF',
            borderRadius: 0
          }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{ 
                borderBottom: '1px solid #FFFFFF',
                '& .MuiTab-root': {
                  color: '#FFFFFF',
                  borderRadius: 0
                },
                '& .Mui-selected': {
                  color: '#FFFFFF !important'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#FFFFFF'
                }
              }}
            >
              <Tab label="ALL VALIDATORS" />
              <Tab label="MY VALIDATORS" />
            </Tabs>
          </Card>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FFFFFF' }} />
              <Typography sx={{ mt: 2, color: '#FFFFFF' }}>Loading validators...</Typography>
            </Box>
          ) : (
            <Box>
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FFFFFF' }}>
                    NETWORK VALIDATORS ({validators.length})
                  </Typography>
                  {validators.length > 0 ? (
                    validators.map((validator) => (
                      <ValidatorCard key={validator.operatorAddress} validator={validator} />
                    ))
                  ) : (
                    <Card sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      backgroundColor: '#000000', 
                      border: '1px solid #FFFFFF',
                      borderRadius: 0
                    }}>
                      <VerifiedUser sx={{ fontSize: 60, color: '#FFFFFF', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, color: '#FFFFFF' }}>
                        NO VALIDATORS FOUND
                      </Typography>
                      <Typography sx={{ mb: 3, color: '#FFFFFF' }}>
                        The network doesn't have any active validators yet.
                      </Typography>
                    </Card>
                  )}
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FFFFFF' }}>
                    MY VALIDATORS ({myValidators.length})
                  </Typography>
                  {myValidators.length > 0 ? (
                    myValidators.map((validator) => (
                      <ValidatorCard key={validator.operatorAddress} validator={validator} isOwned={true} />
                    ))
                  ) : (
                    <Card sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      backgroundColor: '#000000', 
                      border: '1px solid #FFFFFF',
                      borderRadius: 0
                    }}>
                      <Security sx={{ fontSize: 60, color: '#FFFFFF', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, color: '#FFFFFF' }}>
                        NO VALIDATORS FOUND
                      </Typography>
                      <Typography sx={{ mb: 3, color: '#FFFFFF' }}>
                        You don't have any validators yet. Create one to start securing the AVO network.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add sx={{ color: '#FFFFFF' }} />}
                        onClick={() => setCreateDialog(true)}
                        sx={{ 
                          borderColor: '#FFFFFF', 
                          color: '#FFFFFF',
                          borderRadius: 0
                        }}
                      >
                        CREATE VALIDATOR
                      </Button>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Create Validator Dialog */}
        <Dialog 
          open={createDialog} 
          onClose={() => setCreateDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              borderRadius: 0
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: '1px solid #FFFFFF', 
            color: '#FFFFFF' 
          }}>
            CREATE NEW VALIDATOR
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Validator Moniker"
                  value={newValidator.moniker}
                  onChange={(e) => setNewValidator({...newValidator, moniker: e.target.value})}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Commission Rate (%)"
                  value={newValidator.commission}
                  onChange={(e) => setNewValidator({...newValidator, commission: e.target.value})}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Minimum Self Delegation (AVO)"
                  value={newValidator.minSelfDelegation}
                  onChange={(e) => setNewValidator({...newValidator, minSelfDelegation: e.target.value})}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={newValidator.description}
                  onChange={(e) => setNewValidator({...newValidator, description: e.target.value})}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website (optional)"
                  value={newValidator.website}
                  onChange={(e) => setNewValidator({...newValidator, website: e.target.value})}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Security Contact (optional)"
                  value={newValidator.securityContact}
                  onChange={(e) => setNewValidator({...newValidator, securityContact: e.target.value})}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 2, 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                color: '#FFFFFF',
                '& .MuiAlert-icon': {
                  color: '#FFFFFF'
                }
              }}
            >
              Creating a validator requires a minimum self-delegation of {newValidator.minSelfDelegation} AVO tokens. 
              Make sure you have sufficient balance and understand the responsibilities of running a validator.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #FFFFFF' }}>
            <Button 
              onClick={() => setCreateDialog(false)}
              sx={{ 
                color: '#FFFFFF',
                borderRadius: 0
              }}
            >
              CANCEL
            </Button>
            <Button 
              onClick={handleCreateValidator}
              variant="outlined"
              sx={{ 
                borderColor: '#FFFFFF', 
                color: '#FFFFFF',
                borderRadius: 0
              }}
            >
              CREATE VALIDATOR
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default Validators;